import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Alert, Autocomplete, Box, Button, Chip, TextField } from '@mui/material';
import api from 'utils/api';

const categoryId = (category) => String(category?.categoryId ?? '');

export const CategoryInput = ({ control, businessCategories = [], popupZIndex, disabled = false }) => {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);

  useEffect(() => {
    const current = ++request.current;
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      api.get(`/category${query ? `?name=${encodeURIComponent(query)}` : ''}`)
        .then((response) => {
          if (request.current === current) setOptions(response?.content || []);
        })
        .catch(() => {
          if (request.current === current) {
            setOptions([]);
            setError('Categories could not be loaded. Please try again.');
          }
        })
        .finally(() => {
          if (request.current === current) setLoading(false);
        });
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); request.current += 1; };
  }, [query, attempt]);

  return (
    <Box>
      <Controller name="category" control={control} render={({ field: { onChange, value = [] } }) => {
        const available = [...options];
        value.forEach((selected) => {
          if (!available.some((option) => categoryId(option) === categoryId(selected))) available.push(selected);
        });
        return (
          <Autocomplete
            disabled={disabled}
            multiple size="small" loading={loading} options={available} value={value}
            componentsProps={{ popper: { sx: { zIndex: popupZIndex } } }}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, selected) => categoryId(option) === categoryId(selected)}
            filterOptions={(values) => values}
            onChange={(_, selected) => onChange(selected)}
            onInputChange={(_, text, reason) => {
              if (reason === 'input' || reason === 'clear') setQuery(text);
            }}
            renderTags={(tags, getTagProps) => tags.map((option, index) => (
              <Chip {...getTagProps({ index })} key={categoryId(option)} label={option.name}
                color={businessCategories.some((business) => categoryId(business) === categoryId(option)) ? 'primary' : 'default'} />
            ))}
            renderInput={(params) => <TextField {...params} fullWidth label="Categories"
              helperText="Select or search categories from our list." />}
          />
        );
      }} />
      {error && <Alert severity="error" action={<Button disabled={disabled} color="inherit" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>{error}</Alert>}
    </Box>
  );
};

const CategorySelector = ({ control }) => {
  const businessCategories = useSelector((state) => state.business.categories) || [];
  return <CategoryInput control={control} businessCategories={businessCategories} />;
};

export default CategorySelector;
